import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, Icon, PALETTE_KEYS, resolvePalette, Segmented, useToast } from '@/design-system';
import { errorText } from '@/shell/errorText';
import { preferencesKey, savePreferences, type Preferences } from './api';
import s from './ai.module.css';

type Font = NonNullable<Preferences['font']>;
type Tone = NonNullable<Preferences['tone']>;
type Radius = NonNullable<Preferences['radius']>;
type Motion = NonNullable<Preferences['motion']>;
type Lang = 'ru' | 'uz' | 'en';

const FONTS: { key: Font; family: string; glyph: string }[] = [
  { key: 'auto', family: 'var(--font-ui)', glyph: '✦' },
  { key: 'serif', family: 'var(--font-serif)', glyph: 'Aa' },
  { key: 'grotesk', family: 'var(--font-ui)', glyph: 'Aa' },
  { key: 'mono', family: 'var(--font-mono)', glyph: 'Aa' },
];
const TONES: Tone[] = ['auto', 'warm', 'biz', 'prem', 'friend'];
const RADII: Radius[] = ['auto', 'sharp', 'soft', 'round'];
const MOTIONS: Motion[] = ['auto', 'none', 'light', 'rich'];
const LANGS: Lang[] = ['ru', 'uz', 'en'];

export const AUTO: Preferences = { palette: 'auto', font: 'auto', tone: 'auto', radius: 'auto', motion: 'auto', languages: [], slogan: null, cta_text: null, must_mention: null, avoid: null };

type Props = {
  siteId: string;
  prefs: Preferences | undefined;
  brandName: string;
  brandColors: readonly string[] | undefined;
  open: boolean;
  onClose: () => void;
};

/** How long typing waits before the preferences are saved (choices save at once). */
export const PREFS_TEXT_DELAY_MS = 400;

/**
 * «Предпочтения для AI» (DESIGN_SPEC §4.6 Preferences drawer): everything optional; «Пусть AI решает
 * всё» resets to auto. Colours (AI or the 5 palettes), fonts, tone, key copy, corners, motion,
 * languages. A change is in the cache at once (the palette re-skins every thumbnail) and saved per site
 * automatically (PUT /admin/websites/{id}/preferences).
 */
export function PreferencesDrawer({ siteId, prefs, brandName, brandColors, open, onClose }: Props) {
  const { t } = useTranslation('structure');
  const { t: tl } = useTranslation('library');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const toast = useToast();
  const p: Preferences = { ...AUTO, ...prefs };
  const timer = useRef<number | undefined>(undefined);
  const pending = useRef<Preferences | null>(null);
  const [, force] = useState(0);

  const flush = () => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
    const body = pending.current;
    pending.current = null;
    if (!body) return;
    savePreferences(siteId, body)
      .then((out) => {
        if (!pending.current) qc.setQueryData(preferencesKey(siteId), out);
      })
      .catch((e: unknown) => {
        toast(t('pref.save_error', { error: errorText(e, tc) }));
        void qc.invalidateQueries({ queryKey: preferencesKey(siteId) });
      });
  };
  const set = (patch: Partial<Preferences>, delay = 0) => {
    const next = { ...p, ...patch };
    qc.setQueryData(preferencesKey(siteId), (old: object | undefined) => ({ ...old, ...next }));
    pending.current = next;
    force((n) => n + 1);
    window.clearTimeout(timer.current);
    if (delay) timer.current = window.setTimeout(flush, delay);
    else flush();
  };
  const text = (k: 'slogan' | 'cta_text' | 'must_mention' | 'avoid') => ({
    value: p[k] ?? '',
    onChange: (e: { target: { value: string } }) => set({ [k]: e.target.value || null }, PREFS_TEXT_DELAY_MS),
  });
  // Closing saves what is still waiting.
  useEffect(() => {
    if (!open && timer.current !== undefined) flush();
  });
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const langs = (p.languages ?? []) as Lang[];
  const close = () => {
    flush();
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      title={t('pref.title')}
      sub={t('pref.sub')}
      closeLabel={t('pref.close')}
      headerExtra={
        <button type="button" className={s.reset} onClick={() => set(AUTO)}>
          <Icon name="sparkles" size={13} />
          {t('pref.reset')}
        </button>
      }
      footerNote={t('pref.saved')}
      footer={<Button onClick={close}>{t('pref.done')}</Button>}
    >
      <div className={s.group}>
        <span className={s.caps}>{t('pref.colors')}</span>
        <div className={s.palettes} role="radiogroup" aria-label={t('pref.colors')}>
          <button type="button" role="radio" aria-checked={p.palette === 'auto'} className={s.option} onClick={() => set({ palette: 'auto' })}>
            <span className={s.aiTile}>
              <Icon name="sparkles" size={13} />
              {t('pref.palette_ai_tile')}
            </span>
            <span className={s.optionName}>{t('pref.palette_ai')}</span>
          </button>
          {PALETTE_KEYS.map((k) => {
            const pal = resolvePalette(k, brandColors);
            return (
              <button key={k} type="button" role="radio" aria-checked={p.palette === k} className={s.option} onClick={() => set({ palette: k })}>
                <span className={s.strip}>
                  {[pal.bg, pal.ph, pal.acc, pal.ink].map((c, i) => (
                    <span key={i} style={{ background: c }} />
                  ))}
                </span>
                <span className={s.optionName}>{k === 'brand' ? tl('palettes.brand', { name: brandName }) : tl(`palettes.${k}`)}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={s.group}>
        <span className={s.caps}>{t('pref.fonts')}</span>
        <div className={s.fonts} role="radiogroup" aria-label={t('pref.fonts')}>
          {FONTS.map((f) => (
            <button key={f.key} type="button" role="radio" aria-checked={p.font === f.key} className={s.fontOption} onClick={() => set({ font: f.key })}>
              <span className={s.glyph} data-auto={f.key === 'auto'} style={{ fontFamily: f.family }} aria-hidden>
                {f.glyph}
              </span>
              <span className={s.fontName}>{t(`fonts.${f.key}`)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={s.group}>
        <span className={s.caps}>{t('pref.tone')}</span>
        <Segmented variant="compact" label={t('pref.tone')} value={p.tone ?? 'auto'} options={TONES.map((k) => ({ value: k, label: t(`tones.${k}`) }))} onChange={(tone) => set({ tone })} />
      </div>

      <div className={s.groupWide}>
        <span className={s.caps}>{t('pref.copy')}</span>
        <label className={s.label}>
          <span className={s.labelText}>{t('pref.slogan')}</span>
          <input className={s.input} placeholder={t('pref.slogan_ph')} maxLength={300} {...text('slogan')} />
        </label>
        <label className={s.label}>
          <span className={s.labelText}>{t('pref.cta')}</span>
          <input className={s.input} placeholder={t('pref.cta_ph')} maxLength={120} {...text('cta_text')} />
        </label>
        <label className={s.label}>
          <span className={s.labelText}>{t('pref.must')}</span>
          <textarea className={s.textarea} rows={3} placeholder={t('pref.must_ph')} maxLength={2000} {...text('must_mention')} />
        </label>
        <label className={s.label}>
          <span className={s.labelText}>{t('pref.avoid')}</span>
          <input className={s.input} placeholder={t('pref.avoid_ph')} maxLength={2000} {...text('avoid')} />
        </label>
      </div>

      <div className={s.two}>
        <div className={s.group}>
          <span className={s.caps}>{t('pref.radius')}</span>
          <Segmented variant="compact" className={s.seg} label={t('pref.radius')} value={p.radius ?? 'auto'} options={RADII.map((k) => ({ value: k, label: t(`pref.radii.${k}`) }))} onChange={(radius) => set({ radius })} />
        </div>
        <div className={s.group}>
          <span className={s.caps}>{t('pref.motion')}</span>
          <Segmented variant="compact" className={s.seg} label={t('pref.motion')} value={p.motion ?? 'auto'} options={MOTIONS.map((k) => ({ value: k, label: t(`pref.motions.${k}`) }))} onChange={(motion) => set({ motion })} />
        </div>
      </div>

      <div className={s.group}>
        <span className={s.caps}>{t('pref.languages')}</span>
        <div className={s.langs} role="group" aria-label={t('pref.languages')}>
          <button type="button" role="checkbox" aria-checked={langs.length === 0} className={`${s.lang} ${s.langAi}`} onClick={() => set({ languages: [] })}>
            {t('pref.lang_ai')}
          </button>
          {LANGS.map((l) => {
            const on = langs.includes(l);
            return (
              <button key={l} type="button" role="checkbox" aria-checked={on} className={s.lang} onClick={() => set({ languages: on ? langs.filter((x) => x !== l) : [...langs, l] })}>
                {l.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>
    </Drawer>
  );
}
